import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useForm, Controller } from "react-hook-form";
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import once from 'lodash.once';

import * as FormCommon from '../formCommon';

import useSensorStore from '../../store/sensorStore';
import useVendorStore from '../../store/vendorStore';
import useClientStore from '../../store/clientStore';

import useFetchVendors from '../../hooks/useFetchVendors';
import useFetchSensorStatuses from '../../hooks/useFetchSensorStatuses';
import useFetchSensorUseCases from '../../hooks/useFetchSensorUseCases';
import useFetchSensorBillableStatuses from '../../hooks/useFetchSensorBillableStatuses';
import useFetchSensorModels from '../../hooks/useFetchSensorModels';
import useFetchSensorPurchaseOrders from '../../hooks/useFetchSensorPurchaseOrders';
import useFetchClients from '../../hooks/useFetchClients';
import { SensorFormData } from '../../definitions/sensor';

import {
  STATUSES_WITH_CUSTOMER_NEEDED,
  STATUSES_WITH_CUSTOMER_NOT_MANDATORY,
  MOBILE_MEDIA_QUERY_WIDTH,
} from '../../utils/constants';
import {
  FORM_DEFAULT_VALUES,
} from './constants';
import DatePicker from '../DatePicker';
import PageLoader from '../PageLoader';
import Select from '../Select';
import CommentsForm from '../CommentsForm';
import Textarea from '../Textarea';

import { objectCleaner } from '../../utils/helpers';

import {
  prepareVendorSelect,
  prepareVendorContactsSelect,
  preparePurchaseOrdersSelect,
  prepareClientsSelect,
  prepareSiteSelect,
} from './helpers';
import { Autocomplete, Box, createFilterOptions } from '@mui/material';

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: { id: string; description: string }) => option.description,
});

// Selecting it clears the billable status (saved as null).
const BLANK_BILLABLE_STATUS_OPTION = { id: null, description: 'None' };

interface ISensorForm {
  editData?: SensorFormData;
  onSubmitRequest: (data: SensorFormData) => Promise<void> | void;
  submitLoading: boolean;
  mostRecentComment?: string;
}

export default function SensorForm({
  onSubmitRequest,
  submitLoading,
  editData,
  mostRecentComment,
}: ISensorForm) {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_WIDTH);
  const [screenLoading, setScreenLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { fetchSensorsStatuses } = useFetchSensorStatuses();
  const { fetchSensorsUseCases } = useFetchSensorUseCases();
  const { fetchSensorBillableStatuses } = useFetchSensorBillableStatuses();
  const { fetchSensorsModels } = useFetchSensorModels();
  const { fetchSensorPurchaseOrders } = useFetchSensorPurchaseOrders();
  const { fetchClients } = useFetchClients();
  const { fetchVendors } = useFetchVendors();

  const { sensorStatuses, sensorUseCases, sensorBillableStatuses, sensorModels, sensorPurchaseOrders } = useSensorStore();
  const { vendors } = useVendorStore();
  const { clients } = useClientStore();

  const isEdit = !!editData;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({ defaultValues: FORM_DEFAULT_VALUES });

  const sensorModelId = watch('sensorModelId');

  const currentModel = useMemo(() => sensorModels.find((({ id }) => id === sensorModelId)), [sensorModels, sensorModelId]);

  const partDescription = currentModel?.partDescription;

  const vendorId = watch('vendorId');

  const currentVendor = useMemo(() => vendors.find((({ id }) => id === vendorId)), [vendors, vendorId]);

  const vendorContacts = useMemo(() => currentVendor?.contacts || [], [currentVendor]);

  const clientId = watch('clientId');

  const currentClient = useMemo(() => clients.find((({ id }) => id === clientId)), [clients, clientId]);

  const currentSiteId = watch('siteId');

  const sites = useMemo(() => currentClient?.sites || [], [currentClient]);

  const currentSite = useMemo(() => sites.find((({ id }) => id === currentSiteId)), [sites, currentSiteId]);

  const statusId = watch('statusId');

  const currentStatusName = useMemo(() => sensorStatuses.find((({ id }) => id === statusId))?.description, [sensorStatuses, statusId]);

  // When editing a sensor that is already "Installed", its lifecycle is driven from WellManager,
  // so Status / Client / Sensor Location must not be editable here. Base this on the persisted
  // status (editData) rather than the live value so creating/provisioning a new sensor still works.
  const lockInstalledFields = useMemo(() => {
    if (!isEdit || !editData) return false;
    return sensorStatuses.find(({ id }) => id === editData.statusId)?.description === 'Installed';
  }, [isEdit, editData, sensorStatuses]);

  // A sensor can only become "Installed" through WellManager, so that status is never
  // selectable here; it is only kept in the list when the sensor is already Installed,
  // so the (locked) dropdown can display its current value.
  const statusOptions = useMemo(() => {
    if (lockInstalledFields) return sensorStatuses;
    return sensorStatuses.filter(({ description }) => description !== 'Installed');
  }, [lockInstalledFields, sensorStatuses]);

  const onSubmit = async (data: SensorFormData) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    const reqData = {
      ...objectCleaner(data),
      sensorBillableStatusId: data.sensorBillableStatusId ?? null,
    };
    try {
      await onSubmitRequest(reqData as SensorFormData);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const submitDisabled = isSubmitting || submitLoading;

  const loadData = useCallback(once(() => {
    setScreenLoading(true);
    Promise.allSettled([
      fetchSensorsStatuses(),
      fetchSensorsUseCases(),
      fetchSensorBillableStatuses(),
      fetchSensorsModels(),
      fetchVendors(),
      fetchSensorPurchaseOrders(),
      fetchClients(),
    ]).finally(() => setScreenLoading(false))
  }), []);

  useEffect(() => {
    const canReset = isEdit && editData && clients.length > 0 && vendors.length > 0 && sensorModels.length > 0 && sensorStatuses.length > 0 && sensorUseCases.length > 0 && sensorPurchaseOrders.length > 0;
    if (canReset) {
      reset(editData);
    }
  }, [isEdit, editData, clients, vendors, sensorModels, sensorStatuses, sensorUseCases, sensorPurchaseOrders, reset]);

  useEffect(() => {
    if (!currentStatusName) return;
    if (![...STATUSES_WITH_CUSTOMER_NEEDED, ...STATUSES_WITH_CUSTOMER_NOT_MANDATORY].includes(currentStatusName)) {
      setValue('clientId', clients.find(({ isClient }) => !isClient)?.id)
    }
  }, [currentStatusName]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    mostRecentComment && setValue('comment', mostRecentComment)
  }, [mostRecentComment]);

  const onStatusChange = () => {
    setValue('siteId', null);
    setValue('clientId', null);
  };

  const onCustomerChange = () => {
    setValue('siteId', null);
  };

  const isCustomerRequired = useMemo(() => {
    if (!currentStatusName) return;
    return STATUSES_WITH_CUSTOMER_NEEDED.includes(currentStatusName);
  }, [statusId]);

  const statusIsRMA = currentStatusName === 'RMA';

  const isFormReady = useMemo(() => {
    if (!isEdit) return true;
    return isEdit && editData && clients.length > 0 && vendors.length > 0 && sensorModels.length > 0 && sensorStatuses.length > 0 && sensorUseCases.length > 0 && sensorPurchaseOrders.length > 0;
  }, [isEdit, editData, clients, vendors, sensorModels, sensorStatuses, sensorUseCases, sensorPurchaseOrders]);

  const siteOptions = useMemo(() => prepareSiteSelect(sites), [sites]);

  return (!isFormReady || screenLoading) ? (
    <PageLoader />
  ) : (
    <FormCommon.Form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{ overflowX: 'hidden' }}>
        <Grid container spacing={2}>
          {/* Left Column: Sensor Data and Vendor Data */}
          <Grid item xs={12} md={6}>
            <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '16px', marginBottom: '16px' }}>
              <FormCommon.FormBlockTitle>Sensor Data</FormCommon.FormBlockTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="sensorId" sx={{ marginBottom: 0.5, display: 'block' }}>Sensor ID *</Typography>
                  <Controller
                    name="sensorId"
                    control={control}
                    rules={{ required: 'Sensor ID is required' }}
                    render={({ field }) => (
                      <FormCommon.StyledTextField
                        inputProps={{ maxLength: 30, pattern: '[A-Za-z0-9 ]*' }}
                        error={!!errors?.sensorId}
                        helperText={errors?.sensorId?.message}
                        variant="outlined"
                        disabled={!!editData}
                        size="small"
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="statusId" sx={{ marginBottom: 0.5, display: 'block' }}>Status *</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="statusId"
                      control={control}
                      rules={{ required: 'Status is required' }}
                      render={({ field }) => (
                        <Select
                          id="statusId"
                          options={statusOptions}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            onStatusChange();
                          }}
                          error={!!errors?.statusId}
                          helperText={errors?.statusId?.message}
                          required
                          disabled={lockInstalledFields}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>            <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="sensorModelId" sx={{ marginBottom: 0.5, display: 'block' }}>Model Name *</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="sensorModelId"
                      control={control}
                      rules={{ required: 'Model Name is required' }}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          id="sensorModelId"
                          options={sensorModels}
                          filterOptions={filterOptions}
                          getOptionKey={(option) => option.id}
                          getOptionLabel={(option) => option.description}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={sensorModels.find(option => option.id === value) || null}
                          onChange={(_, data) => onChange(data ? data.id : null)}
                          size="small"
                          renderInput={(params) => (
                            <FormCommon.StyledTextField
                              {...params}
                              error={!!errors?.sensorModelId}
                              helperText={errors?.sensorModelId?.message}
                              required
                            />
                          )}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>
                {partDescription && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ marginBottom: 0.5, display: 'block' }}>(Model Name) Partial Description</Typography>
                    <FormCommon.PartDescription>{partDescription}</FormCommon.PartDescription>
                  </Grid>)}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="clientId" sx={{ marginBottom: 0.5, display: 'block' }}>Client *</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="clientId"
                      control={control}
                      rules={{ required: 'Client is required' }}
                      render={({ field }) => (
                        <Select
                          id="clientId"
                          options={prepareClientsSelect(clients, isCustomerRequired)}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            onCustomerChange();
                          }}
                          error={!!errors?.clientId}
                          helperText={errors?.clientId?.message}
                          required
                          disabled={lockInstalledFields}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="siteId" sx={{ marginBottom: 0.5, display: 'block' }}>Sensor Location *</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="siteId"
                      control={control}
                      rules={{ required: 'Sensor Location is required' }}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          id="siteId"
                          options={siteOptions}
                          filterOptions={filterOptions}
                          getOptionKey={(option) => option.id}
                          getOptionLabel={(option) => option.description}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={siteOptions.find(option => option.id === value) || null}
                          onChange={(_, data) => onChange(data ? data.id : null)}
                          disabled={lockInstalledFields}
                          size="small"
                          renderInput={(params) => (
                            <FormCommon.StyledTextField
                              {...params}
                              error={!!errors?.siteId}
                              helperText={errors?.siteId?.message}
                              required
                            />
                          )}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="useCaseId" sx={{ marginBottom: 0.5, display: 'block' }}>Use Case {currentStatusName === "Installed" && ' *'}</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="useCaseId"
                      control={control}
                      rules={{ required: currentStatusName === "Installed" ? 'Use Case is required' : false }}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          id="useCaseId"
                          options={sensorUseCases}
                          filterOptions={filterOptions}
                          getOptionKey={(option) => option.id}
                          getOptionLabel={(option) => option.description}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={sensorUseCases.find(option => option.id === value) || null}
                          onChange={(_, data) => onChange(data ? data.id : null)}
                          size="small"
                          renderInput={(params) => (
                            <FormCommon.StyledTextField
                              {...params}
                              error={!!errors?.useCaseId}
                              helperText={errors?.useCaseId?.message}
                              required={currentStatusName === "Installed"}
                            />
                          )}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="sensorBillableStatusId" sx={{ marginBottom: 0.5, display: 'block' }}>Billable Status</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="sensorBillableStatusId"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          id="sensorBillableStatusId"
                          options={[BLANK_BILLABLE_STATUS_OPTION, ...sensorBillableStatuses]}
                          filterOptions={filterOptions}
                          getOptionKey={(option) => option.id ?? 'blank'}
                          getOptionLabel={(option) => option.description}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={sensorBillableStatuses.find(option => option.id === value) || null}
                          onChange={(_, data) => onChange(data ? data.id : null)}
                          size="small"
                          renderInput={(params) => (
                            <FormCommon.StyledTextField {...params} />
                          )}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>            </Grid>
            </Box>

            <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '16px', marginBottom: '16px' }}>
              <FormCommon.FormBlockTitle>Vendor Data</FormCommon.FormBlockTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="vendorId" sx={{ marginBottom: 0.5, display: 'block' }}>Vendor *</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="vendorId"
                      control={control}
                      rules={{ required: 'Vendor is required' }}
                      render={({ field }) => (
                        <Select
                          id="vendorId"
                          options={prepareVendorSelect(vendors)}
                          value={field.value}
                          onChange={field.onChange}
                          required
                          error={!!errors?.vendorId}
                          helperText={errors?.vendorId?.message}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>              {(!vendorContacts.length && vendorId) ? (
                  <Grid item xs={12} md={6}>
                    <FormCommon.PartDescription
                      style={{ marginBottom: 15, marginTop: 5 }}
                    >
                      There are no contacts for this vendor
                    </FormCommon.PartDescription>
                  </Grid>
                ) : null}
                {vendorContacts.length ? (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="vendorContactId" sx={{ marginBottom: 0.5, display: 'block' }}>Vendor Contact</Typography>
                    <FormCommon.SelectWrap>
                      <Controller
                        name="vendorContactId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            id="vendorContactId"
                            options={prepareVendorContactsSelect(vendorContacts)}
                            value={field.value}
                            onChange={field.onChange}
                            error={!!errors?.vendorContactId}
                          />
                        )}
                      />
                    </FormCommon.SelectWrap>
                  </Grid>) : null}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="leadTime" sx={{ marginBottom: 0.5, display: 'block' }}>Lead Time</Typography>
                  <FormCommon.StyledTextField
                    id="leadTime"
                    variant="outlined"
                    size="small"
                    {...register("leadTime", { required: false })}
                    error={!!errors?.leadTime}
                  />
                </Grid>            </Grid>
            </Box>
          </Grid>

          {/* Right Column: Additional Data */}
          <Grid item xs={12} md={6}>
            <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '16px', marginBottom: '16px' }}>
              <FormCommon.FormBlockTitle>Additional Data</FormCommon.FormBlockTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="receivedDate" sx={{ marginBottom: 0.5, display: 'block' }}>Received Date *</Typography>
                  <FormCommon.DatePickerWrap>
                    <Controller
                      name="receivedDate"
                      control={control}
                      rules={{ required: 'Received Date is required' }}
                      render={({ field }) => (
                        <DatePicker
                          defaultValue={editData?.receivedDate}
                          error={!!errors?.receivedDate}
                          helperText={errors?.receivedDate?.message}
                          setValue={(_: string, value: unknown) => field.onChange(value)}
                          id="receivedDate"
                          required
                        />
                      )}
                    />
                  </FormCommon.DatePickerWrap>
                </Grid>                          <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="cost" sx={{ marginBottom: 0.5, display: 'block' }}>Cost *</Typography>
                  <FormCommon.StyledTextField
                    id="outlined-basic"
                    variant="outlined"
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: 'any' }}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    {...register("cost", {
                      required: 'Cost is required',
                      min: { value: 0, message: 'Cost must not be negative' },
                    })}
                    error={!!errors?.cost}
                    helperText={errors?.cost?.message}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="purchaseOrderId" sx={{ marginBottom: 0.5, display: 'block' }}>PO Number</Typography>
                  <FormCommon.SelectWrap>
                    <Controller
                      name="purchaseOrderId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          id="purchaseOrderId"
                          options={preparePurchaseOrdersSelect(sensorPurchaseOrders)}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!errors?.purchaseOrderId}
                          helperText={errors?.purchaseOrderId?.message}
                        />
                      )}
                    />
                  </FormCommon.SelectWrap>
                </Grid>            <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" component="label" htmlFor="installDate" sx={{ marginBottom: 0.5, display: 'block' }}>Original Installed Date {currentStatusName === 'Installed' && ' *'}</Typography>
                  <FormCommon.DatePickerWrap>
                    <Controller
                      name="installDate"
                      control={control}
                      rules={{ required: currentStatusName === 'Installed' ? 'Original Installed Date is required' : false }}
                      render={({ field }) => (
                        <DatePicker
                          setValue={(_: string, value: unknown) => field.onChange(value)}
                          id="installDate"
                          error={!!errors?.installDate}
                          helperText={errors?.installDate?.message}
                          required={currentStatusName === 'Installed'}
                          defaultValue={editData?.installDate}
                        />
                      )}
                    />
                  </FormCommon.DatePickerWrap>
                </Grid>
                {editData?.warrantyExpiration ? (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="warrantyExpiration" sx={{ marginBottom: 0.5, display: 'block' }}>Warranty Expiration Date</Typography>
                    <FormCommon.DatePickerWrap>
                      <DatePicker
                        defaultValue={editData.warrantyExpiration}
                        setValue={setValue}
                        id="warrantyExpiration"
                        disabled
                      />
                    </FormCommon.DatePickerWrap>
                  </Grid>) : currentVendor ? (
                    <Grid item xs={12} md={6}>
                      <div style={{ marginBottom: 15, marginTop: 5 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 0.5, display: 'block' }}>Warranty Expiration Date</Typography>
                        <FormCommon.PartDescription>
                          1 year from received date
                        </FormCommon.PartDescription>
                      </div>
                    </Grid>) : null}
                {currentStatusName === 'Written Off' && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="writtenOffDate" sx={{ marginBottom: 0.5, display: 'block' }}>Written Off Date</Typography>
                    <FormCommon.DatePickerWrap>
                      <DatePicker
                        setValue={setValue}
                        id="writtenOffDate"
                        defaultValue={editData?.writtenOffDate}
                      />
                    </FormCommon.DatePickerWrap>
                  </Grid>)}

                {statusIsRMA && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="RMAReturnedToSupplierDate" sx={{ marginBottom: 0.5, display: 'block' }}>RMA Returned To Supplier Date</Typography>
                    <FormCommon.DatePickerWrap>
                      <DatePicker
                        setValue={setValue}
                        id="RMAReturnedToSupplierDate"
                        defaultValue={editData?.rmaReturnedToSupplierDate}
                      />
                    </FormCommon.DatePickerWrap>
                  </Grid>)}
                {statusIsRMA && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="RMAReturnedFromSupplierDate" sx={{ marginBottom: 0.5, display: 'block' }}>RMA Returned From Supplier Date</Typography>
                    <FormCommon.DatePickerWrap>
                      <DatePicker
                        setValue={setValue}
                        id="RMAReturnedFromSupplierDate"
                        defaultValue={editData?.rmaReturnedFromSupplierDate}
                      />
                    </FormCommon.DatePickerWrap>
                  </Grid>)}
                {statusIsRMA && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="rmaReplacementSensorId" sx={{ marginBottom: 0.5, display: 'block' }}>RMA Replacement Sensor Id</Typography>
                    <FormCommon.StyledTextField
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      {...register("rmaReplacementSensorId", { required: false })}
                      error={!!errors?.rmaReplacementSensorId}
                    />
                  </Grid>)}
                {statusIsRMA && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" component="label" htmlFor="rmaNumber" sx={{ marginBottom: 0.5, display: 'block' }}>RMA Number</Typography>
                    <FormCommon.StyledTextField
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      {...register("rmaNumber", { required: false })}
                      error={!!errors?.rmaNumber}
                    />
                  </Grid>)}
                <Grid item xs={12}>
                  {isEdit && mostRecentComment ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                      <div style={{ flexGrow: 1 }}>
                        {/* <Typography variant="subtitle2" sx={{ marginBottom: 0.5, display: 'block' }}>Most Recent Comment</Typography>
                        <FormCommon.PartDescription>{mostRecentComment}</FormCommon.PartDescription> */}
                      </div>
                      <CommentsForm />
                    </Box>) : isEdit ? (
                      <CommentsForm />
                    ) : (
                    <Box>
                      <Typography variant="subtitle2" component="label" htmlFor="comment" sx={{ marginBottom: 0.5, display: 'block' }}>Comment</Typography>
                      <Textarea
                        aria-label="Add comment here"
                        placeholder="Add comment here"
                        {...register("comment", { required: false })}
                      />
                    </Box>
                  )}
                </Grid>            </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <FormCommon.SubmitBtn sx={{ width: '100%' }} type="submit" disabled={submitDisabled}>
              {submitDisabled ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
            </FormCommon.SubmitBtn>
          </Grid>
        </Grid>
      </Box>
    </FormCommon.Form>
  );
}
